/**
 * CycleLandingCard Component
 * 
 * Reusable card component for displaying cycle summary information.
 * Supports both "start" (landing) and "complete" (completion) modes.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Store, Briefcase, CheckSquare, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';

interface CycleLandingCardProps {
  mode: "start" | "complete";
  cycleName: string;
  period: string;
  retailerName: string;
  totalStores: number;
  totalCampaigns: number;
  totalTasks: number;
  campaigns: Array<{
    id: number;
    name: string;
    storeCount: number;
    taskCount: number;
  }>;
  // Complete mode only:
  completedAt?: string | null;
  signedOffBy?: string | null;
  onAction?: () => void; // "Start Route" or "View Gallery"
  onBack?: () => void; // Optional back navigation for complete mode
}

export function CycleLandingCard({
  mode,
  cycleName,
  period,
  retailerName,
  totalStores,
  totalCampaigns,
  totalTasks,
  campaigns,
  completedAt,
  signedOffBy,
  onAction,
  onBack,
}: CycleLandingCardProps) {
  // Format completion date for display
  const formatCompletedDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Format as "24 Nov 2025" (day month year, no time)
      return format(date, 'd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const formattedCompletedAt = formatCompletedDate(completedAt);

  return (
    <div className="p-4 space-y-4 bg-white min-h-screen pb-24">
      {/* Back button for complete mode */}
      {mode === "complete" && onBack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Current Route
        </Button>
      )}
      
      {/* Header Card */}
      <Card className="rounded-xl shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-2xl font-bold">
            {mode === "complete" ? "Cycle Completed" : cycleName}
          </CardTitle>
          {mode === "complete" && (
            <p className="text-lg font-semibold text-gray-700 mt-1">{cycleName}</p>
          )}
          <p className="text-gray-600 text-sm mt-1">{period}</p>
          <p className="text-gray-500 text-sm">{retailerName}</p>
        </CardHeader>
      </Card>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="rounded-xl shadow-sm">
          <CardContent className="p-4 text-center">
            <Store className="h-6 w-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold text-gray-900">{totalStores}</div>
            <div className="text-xs text-gray-500 mt-1">Stores</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl shadow-sm">
          <CardContent className="p-4 text-center">
            <Briefcase className="h-6 w-6 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold text-gray-900">{totalCampaigns}</div>
            <div className="text-xs text-gray-500 mt-1">Campaigns</div>
          </CardContent>
        </Card>
        <Card className="rounded-xl shadow-sm">
          <CardContent className="p-4 text-center">
            <CheckSquare className="h-6 w-6 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold text-gray-900">{totalTasks}</div>
            <div className="text-xs text-gray-500 mt-1">Tasks</div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign List */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Campaigns</h2>
        {campaigns.length === 0 ? (
          <Card className="rounded-xl shadow-sm">
            <CardContent className="p-6 text-center text-gray-500">
              No campaigns assigned
            </CardContent>
          </Card>
        ) : (
          campaigns.map((campaign) => (
            <Card key={campaign.id} className="rounded-xl shadow-sm">
              <CardContent className="p-4">
                <div className="font-semibold text-gray-900">{campaign.name}</div>
                <div className="text-sm text-gray-500 mt-1">
                  {campaign.storeCount} {campaign.storeCount === 1 ? 'store' : 'stores'} · {campaign.taskCount} {campaign.taskCount === 1 ? 'task' : 'tasks'}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Completion Message (complete mode only) */}
      {mode === "complete" && (
        <div className="mt-4 text-center text-muted-foreground">
          <p className="text-base font-medium text-gray-900 mb-2">
            All stores and campaigns for this cycle are complete.
          </p>
          {formattedCompletedAt && signedOffBy && (
            <p className="text-sm text-gray-600">
              Completed on {formattedCompletedAt} by {signedOffBy}
            </p>
          )}
          {formattedCompletedAt && !signedOffBy && (
            <p className="text-sm text-gray-600">
              Completed on {formattedCompletedAt}
            </p>
          )}
          {!formattedCompletedAt && signedOffBy && (
            <p className="text-sm text-gray-600">
              Signed off by {signedOffBy}
            </p>
          )}
        </div>
      )}

      {/* Sticky Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 safe-area-bottom">
        <Button
          onClick={onAction}
          className="w-full h-12 text-base font-semibold"
          size="lg"
        >
          {mode === "complete" ? "View Gallery" : "Start Route"}
        </Button>
      </div>
    </div>
  );
}

